/**
 * The client for the authentication API.
 *
 * Three things here are not incidental:
 *
 * - **`credentials: 'include'`** on every call. The session lives in a cookie the browser
 *   will not send to another origin unless asked, and since the API is on a different
 *   host from the page, every single request has to ask.
 * - **The CSRF token**, held in memory here rather than in a cookie or in `localStorage`.
 *   In memory it is unreachable by another site and gone when the tab closes, which is
 *   precisely the property that makes echoing it back a proof of origin.
 * - **Responses are checked, not cast.** The server is ours, but `as` on a parsed body is
 *   a lie the type checker cannot catch, and a deployment mismatch would then surface as
 *   `undefined` somewhere far away instead of as an error here.
 */

import { buildStampFrom, type BuildStamp } from '../domain/version.js';
import { logger } from '../log.js';

const log = logger('accounts');

export interface Account {
  readonly id: string;
  readonly email: string;
  /** RFC 3339, as an unparsed string. Formatting is the UI's business. */
  readonly createdAt: string;
}

export interface SignedIn {
  readonly user: Account;
  /** When this account was last signed in to before now; absent on a first sign-in. */
  readonly previousLogin: string | undefined;
}

export interface LoginEvent {
  readonly at: string;
}

/** The API answered, and said no. */
export class ApiError extends Error {
  constructor(
    /** Stable identifier — branch on this, never on the message. */
    readonly code: string,
    message: string,
    readonly status: number,
    readonly retryAfterSeconds: number | undefined,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The API could not be reached at all: no server, no DNS, a rejected certificate, or a
 * CORS refusal. Distinct from [`ApiError`] because it means something quite different to
 * the user — nothing is wrong with what they typed.
 */
export class UnreachableError extends Error {
  constructor(cause: unknown) {
    super('The account service could not be reached.');
    this.name = 'UnreachableError';
    this.cause = cause;
  }
}

type Fetch = (input: string, init: RequestInit) => Promise<Response>;

export class AuthApi {
  /** Held only in memory. See the note at the top of the file. */
  private csrfToken: string | undefined;

  constructor(
    private readonly baseUrl: string,
    private readonly fetchImpl: Fetch = (input, init) => fetch(input, init),
  ) {}

  /**
   * Who is signed in, if anyone.
   *
   * Being signed out is an ordinary answer rather than an error, so this returns
   * `undefined` instead of throwing — a signed-out visitor is the common case, not a
   * failure.
   */
  async currentAccount(): Promise<Account | undefined> {
    const body = await this.send('GET', '/api/auth/session');

    const user = readOptionalRecord(body, 'user');
    if (!user) {
      this.csrfToken = undefined;
      return undefined;
    }

    // Picked up here as well as on sign-in, so a page reload restores the ability to make
    // state-changing calls without signing in again.
    this.csrfToken = readOptionalString(body, 'csrfToken');
    return readAccount(user);
  }

  async register(email: string, password: string): Promise<SignedIn> {
    return this.signIn('/api/auth/register', email, password);
  }

  async logIn(email: string, password: string): Promise<SignedIn> {
    return this.signIn('/api/auth/login', email, password);
  }

  async logOut(): Promise<void> {
    await this.send('POST', '/api/auth/logout');
    this.csrfToken = undefined;
  }

  /**
   * The CSRF token, for the one caller that cannot send it as a header.
   *
   * A WebSocket handshake from a browser carries no custom headers, so the signalling
   * connection proves itself with this in its first message instead. Handing it out inside
   * the page changes nothing about what protects it: the token has always lived in this
   * page's memory, and what makes echoing it back meaningful is that it is *not* in a
   * cookie or in `localStorage` where another origin could reach it.
   */
  currentCsrfToken(): string | undefined {
    return this.csrfToken;
  }

  /**
   * Which build the service is running, or `undefined` if it will not say.
   *
   * Never throws, and the three ways of getting nothing are deliberately not told apart
   * here: unreachable, an answer in some shape we do not recognise, and a service that
   * was itself built without a stamp all mean the same thing to the one caller — that
   * there is no second build number to show beside this page's own. The console
   * distinguishes them for whoever is deploying, which is the only person the difference
   * helps.
   *
   * Unauthenticated on purpose. The About panel is open to a visitor who has never signed
   * in, and a version that only signed-in people could see would be missing exactly when
   * somebody is trying to work out why signing in does not work.
   */
  async serviceBuild(): Promise<BuildStamp | undefined> {
    let body: unknown;
    try {
      body = await this.send('GET', '/api/health');
    } catch (error) {
      log.info('The account service did not say which build it is running.', error);
      return undefined;
    }

    const stamp = isRecord(body) ? buildStampFrom(body.build, body.commit) : undefined;

    if (stamp === undefined) {
      log.info(
        'The account service answered /api/health without a build it could be ' +
          'identified by. A service built outside the packaging targets is unstamped, ' +
          'and so is one being answered for by something that is not it.',
      );
    }

    return stamp;
  }

  /** Recent sign-ins for the signed-in account, newest first. */
  async loginHistory(): Promise<LoginEvent[]> {
    const body = await this.send('GET', '/api/auth/logins');

    const logins = isRecord(body) ? body.logins : undefined;
    if (!Array.isArray(logins)) {
      throw new Error('The account service returned an unrecognised sign-in history.');
    }

    return logins.map((entry) => ({ at: requireString(entry, 'at') }));
  }

  private async signIn(path: string, email: string, password: string): Promise<SignedIn> {
    const body = await this.send('POST', path, { email, password });

    const user = isRecord(body) ? body.user : undefined;
    if (!isRecord(user)) {
      throw new Error('The account service returned an unrecognised sign-in response.');
    }

    this.csrfToken = readOptionalString(body, 'csrfToken');

    return {
      user: readAccount(user),
      previousLogin: readOptionalString(body, 'previousLogin'),
    };
  }

  private async send(
    method: 'GET' | 'POST',
    path: string,
    body?: unknown,
  ): Promise<unknown> {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['content-type'] = 'application/json';
    // Only state-changing requests need it, and sending it on reads would add a CORS
    // preflight to every one of them for nothing.
    if (method !== 'GET' && this.csrfToken !== undefined) {
      headers['x-csrf-token'] = this.csrfToken;
    }

    let response: Response;
    try {
      response = await this.fetchImpl(new URL(path, this.baseUrl).toString(), {
        method,
        headers,
        credentials: 'include',
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch (error) {
      throw new UnreachableError(error);
    }

    // 204, and anything else with nothing to say.
    const text = await response.text();
    const parsed: unknown = text === '' ? undefined : safeParse(text);

    if (!response.ok) throw toApiError(response, parsed);
    return parsed;
  }
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

function toApiError(response: Response, body: unknown): ApiError {
  const retryAfter = Number(response.headers.get('retry-after'));

  return new ApiError(
    readOptionalString(body, 'code') ?? 'unknown',
    // A body we cannot read means something other than the API answered — a proxy error
    // page, most likely — so say something true rather than showing a status code.
    readOptionalString(body, 'message') ?? 'The account service returned an error.',
    response.status,
    Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readOptionalRecord(
  value: unknown,
  key: string,
): Record<string, unknown> | undefined {
  const field = isRecord(value) ? value[key] : undefined;
  return isRecord(field) ? field : undefined;
}

function readOptionalString(value: unknown, key: string): string | undefined {
  const field = isRecord(value) ? value[key] : undefined;
  return typeof field === 'string' ? field : undefined;
}

function requireString(value: unknown, key: string): string {
  const field = readOptionalString(value, key);
  if (field === undefined) {
    throw new Error(`The account service omitted "${key}".`);
  }
  return field;
}

function readAccount(user: Record<string, unknown>): Account {
  return {
    id: requireString(user, 'id'),
    email: requireString(user, 'email'),
    createdAt: requireString(user, 'createdAt'),
  };
}
